import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchEmployees, createEmployee, updateEmployee, deleteEmployee, getEmpleadoById } from '../api/adminService';
import { fetchProjects, fetchProjectDocuments, getDocumentsByEmpleado } from '../api/administrationService'

const EMPLOYEES_QUERY_KEY = ["employees"];

const normalizeEmployeeResponse = (response) => {
  return response?.data ?? response;
};

/*export const useEmployees = () => {
  const queryClient = useQueryClient();

  const { 
    data: employees = [], 
    error, 
    isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: fetchEmployees,
  });

  const createMutation = useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateEmployee(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });

  return {
    employees,
    error,
    isLoading,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};*/

export const useEmployees = () => {
  const queryClient = useQueryClient();

  const employeesQuery = useQuery({
    queryKey: EMPLOYEES_QUERY_KEY,
    queryFn: fetchEmployees,
    staleTime: 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: createEmployee,

    onSuccess: (response) => {
      const employee =
        normalizeEmployeeResponse(response);

      queryClient.invalidateQueries({
        queryKey: EMPLOYEES_QUERY_KEY,
      });

      if (employee?.empid) {
        queryClient.setQueryData(
          ["empleado", employee.empid],
          employee
        );
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) =>
      updateEmployee(id, data),

    onSuccess: (response, variables) => {
      const employee =
        normalizeEmployeeResponse(response);

      queryClient.invalidateQueries({
        queryKey: EMPLOYEES_QUERY_KEY,
      });

      queryClient.invalidateQueries({
        queryKey: [
          "empleado",
          Number(variables.id),
        ],
      });

      if (employee?.empid) {
        queryClient.setQueryData(
          ["empleado", employee.empid],
          employee
        );
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmployee,

    onSuccess: (_, employeeId) => {
      queryClient.invalidateQueries({
        queryKey: EMPLOYEES_QUERY_KEY,
      });

      queryClient.removeQueries({
        queryKey: [
          "empleado",
          Number(employeeId),
        ],
      });
    },
  });

  return {
    employees: employeesQuery.data ?? [],
    error: employeesQuery.error,
    isLoading: employeesQuery.isLoading,
    isFetching: employeesQuery.isFetching,

    createMutation,
    updateMutation,
    deleteMutation,
  };
};

/*export const useEmpleadoDetalle = (empID) => {
  const empleadoQuery = useQuery({
    queryKey: ['empleado', empID],
    queryFn: () => getEmpleadoById(empID),
    enabled: !!empID,
  });

  const proyectosQuery = useQuery({
    queryKey: ['proyectos'],
    queryFn: fetchProjects,
  });

  const documentosEmpleadoQuery = useQuery({
    queryKey: ['documentosEmpleado', empID],
    queryFn: () => getDocumentsByEmpleado(empID),
    enabled: !!empID,
  });

  return {
    empleado: empleadoQuery.data,
    proyectos: proyectosQuery.data || [],
    documentosEmpleado: documentosEmpleadoQuery.data || [],
    isLoading: empleadoQuery.isLoading || proyectosQuery.isLoading || documentosEmpleadoQuery.isLoading,
    error: empleadoQuery.error || proyectosQuery.error || documentosEmpleadoQuery.error,
    fetchProjectDocuments,
    refetchDocumentosEmpleado: documentosEmpleadoQuery.refetch,
  };
};*/

export const useEmpleadoDetalle = (empID) => {
  const employeeId = Number(empID);

  const empleadoQuery = useQuery({
    queryKey: ["empleado", employeeId],
    queryFn: () =>
      getEmpleadoById(employeeId),
    enabled:
      Number.isInteger(employeeId) &&
      employeeId > 0,
    staleTime: 60 * 1000,
  });

  const documentosEmpleadoQuery = useQuery({
    queryKey: [
      "documentosEmpleado",
      employeeId,
    ],
    queryFn: () =>
      getDocumentsByEmpleado(employeeId),
    enabled:
      Number.isInteger(employeeId) &&
      employeeId > 0,
  });

  const empleado = empleadoQuery.data;

  const serviciosProyecto =
    empleado?.serviciosProyecto ??
    empleado?.ServiciosProyecto ??
    [];

  /*
   * Se deriva una lista única de proyectos desde los
   * servicios-proyecto realmente asociados al empleado.
   */
  const proyectosMap = new Map();

  for (const servicio of serviciosProyecto) {
    const proID = Number(
      servicio.proid ??
      servicio.ProID
    );

    if (!Number.isInteger(proID)) {
      continue;
    }

    if (!proyectosMap.has(proID)) {
      proyectosMap.set(proID, {
        proid: proID,
        pronom:
          servicio.pronom ??
          servicio.ProNom ??
          "Proyecto",
      });
    }
  }

  const proyectos = [
    ...proyectosMap.values(),
  ].sort((a, b) =>
    String(a.pronom).localeCompare(
      String(b.pronom)
    )
  );

  return {
    empleado,
    serviciosProyecto,
    proyectos,

    documentosEmpleado:
      documentosEmpleadoQuery.data ?? [],

    isLoading:
      empleadoQuery.isLoading ||
      documentosEmpleadoQuery.isLoading,

    isFetching:
      empleadoQuery.isFetching ||
      documentosEmpleadoQuery.isFetching,

    error:
      empleadoQuery.error ||
      documentosEmpleadoQuery.error,

    fetchProjectDocuments,

    refetchEmpleado:
      empleadoQuery.refetch,

    refetchDocumentosEmpleado:
      documentosEmpleadoQuery.refetch,
  };
};